// src/components/teams/components/modals/incomeBreakdownModal.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, where, documentId, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { getCurrencyMeta } from '../../../../utils/currency';
import { getStripeFeeConfig, getPlatformFeeMinor } from '../../../../utils/fees';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Brush, PieChart, Pie, Cell } from 'recharts';
import { saveAs } from 'file-saver';
const COLORS = ['#4CAF50', '#F44336'];
const MONTH_OPTIONS = [{
  value: 'All',
  label: 'All Months'
}, {
  value: '01',
  label: 'Jan'
}, {
  value: '02',
  label: 'Feb'
}, {
  value: '03',
  label: 'Mar'
}, {
  value: '04',
  label: 'Apr'
}, {
  value: '05',
  label: 'May'
}, {
  value: '06',
  label: 'Jun'
}, {
  value: '07',
  label: 'Jul'
}, {
  value: '08',
  label: 'Aug'
}, {
  value: '09',
  label: 'Sep'
}, {
  value: '10',
  label: 'Oct'
}, {
  value: '11',
  label: 'Nov'
}, {
  value: '12',
  label: 'Dec'
}];

/* ───────────────────────────── Helpers (fees & dates) ───────────────────────────── */

const estimateStripeFeeMinor = (amountMinor, currency) => {
  const cfg = getStripeFeeConfig({
    currency
  });
  const pct = Number(cfg.percent || 0);
  const fixed = Number(cfg.fixedMinor || 0);
  const fee = Math.round(Number(amountMinor || 0) * pct + fixed);
  return Math.max(0, fee);
};
function platformFeePerCycle({
  renewalEvery = 1,
  renewalUnit = 'year',
  platformFeeMinor
} = {}) {
  // Fallback for legacy rows that don't have a breakdown
  const cyclesMap = {
    year: 1,
    month: 12,
    week: 52,
    day: 365
  };
  const cycles = cyclesMap[renewalUnit] || 1;
  return Math.round(Number(platformFeeMinor || 0) * Number(renewalEvery || 1) / cycles);
}
function payoutFromBreakdown({
  breakdown = {},
  platformFeeHandling,
  stripeFeeHandling,
  currency
}) {
  // breakdown = { baseAmount, platformFee, stripeFee, finalAmount }
  // We compute CLUB payout (what the org receives), not what member pays.
  const baseAmount = Number(breakdown.baseAmount || 0);
  const platformFee = Number(breakdown.platformFee || 0);
  const hasStoredStripeFee = Object.prototype.hasOwnProperty.call(breakdown, 'stripeFee');
  const storedStripeFee = Number(breakdown.stripeFee || 0);

  // Stripe deduction only if handling == 'deduct'
  const grossForFees = baseAmount + (platformFeeHandling === 'add' ? platformFee : 0);
  const stripeDeduction = stripeFeeHandling === 'deduct' ? hasStoredStripeFee ? storedStripeFee : estimateStripeFeeMinor(grossForFees, currency) : 0;
  const platformDeduction = platformFeeHandling === 'deduct' ? platformFee : 0;
  const payout = baseAmount - platformDeduction - stripeDeduction;
  return Math.max(0, payout);
}
function computeExpectedClubPayout({
  baseAmountPence,
  platformFeeHandling,
  // 'add' | 'deduct'
  stripeFeeHandling,
  // 'add' | 'deduct'
  perCyclePlatformFeePence,
  currency
}) {
  const base = Number(baseAmountPence || 0);
  const perCycle = Number(perCyclePlatformFeePence || 0);
  const grossMemberPays = platformFeeHandling === 'add' ? base + perCycle : base;
  const stripeFee = stripeFeeHandling === 'deduct' ? estimateStripeFeeMinor(grossMemberPays, currency) : 0;
  const platformDeduction = platformFeeHandling === 'deduct' ? perCycle : 0;
  const payout = base - platformDeduction - stripeFee;
  return Math.max(0, payout);
}
function computePaidClubPayout({
  amountPence,
  chargedAmountPence,
  // optional (bolt-on manual)
  isMembership,
  isBoltOn,
  platformFeeHandling,
  stripeFeeHandling,
  perCyclePlatformFeePence,
  trustPaidAmount = false,
  currency
}) {
  if (trustPaidAmount) return Math.max(0, Number(amountPence || 0));

  // Legacy approximation when no breakdown exists
  let base = Number(amountPence || 0);
  if (isMembership) {
    if (platformFeeHandling === 'add') {
      base = Math.max(0, base - Number(perCyclePlatformFeePence || 0));
    }
  } else if (isBoltOn) {
    base = Number(amountPence || 0);
  }
  return computeExpectedClubPayout({
    baseAmountPence: base,
    platformFeeHandling,
    stripeFeeHandling,
    perCyclePlatformFeePence,
    currency
  });
}
function formatCycleLabel(cycleId) {
  const [year, month] = String(cycleId || '').split('-');
  const date = new Date(`${year}-${month || '01'}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return cycleId || '';
  return date.toLocaleString('en-GB', {
    month: 'short',
    year: 'numeric'
  });
}
const safeToDate = ts => ts && typeof ts.toDate === 'function' ? ts.toDate() : ts ? new Date(ts) : null;

/* ─────────────────────────────────── Component ─────────────────────────────────── */

export default function IncomeBreakdownModal({
  isOpen,
  onClose,
  teamId
}) {
  const [allCycles, setAllCycles] = useState([]); // [{ name:'YYYY-MM', label, Expected, Paid }]
  const [allOutstanding, setAllOutstanding] = useState([]); // [{ id, amount, displayAmount, ... }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [currency, setCurrency] = useState('GBP');
  const currencyMeta = useMemo(() => getCurrencyMeta(currency), [currency]);
  const formatMinor = minor => {
    const {
      symbol,
      decimals
    } = currencyMeta;
    const major = Number(minor || 0) / Math.pow(10, decimals);
    return `${symbol}${major.toFixed(decimals)}`;
  };
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        /* ── Bolt-ons (names & fee policy overrides) ── */
        const boltOnsSnap = await getDocs(collection(db, 'teams', teamId, 'boltOns'));
        const boltOnById = {};
        boltOnsSnap.forEach(bs => {
          const d = bs.data() || {};
          boltOnById[bs.id] = {
            name: d.name || d.title || bs.id,
            stripeFeeHandling: d.stripeFeeHandling || 'deduct'
          };
        });

        /* ── Team setup (membership fee policy + legacy cadence fallback) ── */
        const setupSnap = await getDoc(doc(db, `teams/${teamId}/stripe/setup`));
        const setup = setupSnap.exists() ? setupSnap.data() || {} : {};
        const setupCurrency = setup.currency || 'GBP';
        setCurrency(setupCurrency);
        const perYearPlatformFeeMinor = getPlatformFeeMinor({
          currency: setupCurrency,
          teamFeeCfg: setup
        });
        const teamPolicy = {
          platformFeeHandling: setup.platformFeeHandling || 'add',
          stripeFeeHandling: setup.stripeFeeHandling || 'deduct',
          renewalEvery: setup.renewalEvery || 1,
          renewalUnit: setup.renewalUnit || 'year'
        };
        const feePerCycleFallback = platformFeePerCycle({
          ...teamPolicy,
          platformFeeMinor: setup.platformFeeMinor ?? perYearPlatformFeeMinor
        });

        /* ── Iterate billing cycles & members ── */
        const cyclesSnap = await getDocs(collection(db, 'teams', teamId, 'billingCycles'));
        const totalsByCycle = {}; // { 'YYYY-MM': { Expected, Paid } }
        const outstandingRows = [];
        const memberIdsNeeded = new Set();
        const bumpCycle = (cycleId, key, value) => {
          if (!totalsByCycle[cycleId]) totalsByCycle[cycleId] = {
            Expected: 0,
            Paid: 0
          };
          totalsByCycle[cycleId][key] += Number(value || 0);
        };
        for (const cdoc of cyclesSnap.docs) {
          const cycleId = cdoc.id;
          const membSnap = await getDocs(collection(db, 'teams', teamId, 'billingCycles', cycleId, 'members'));
          membSnap.forEach(mdoc => {
            const row = mdoc.data() || {};
            const uid = row.uid || mdoc.id;
            const paid = !!row.paid;
            const method = row.method; // 'manual' | 'subscription'
            const paymentType = row.paymentType || (mdoc.id.includes('__bo__') ? 'bolt_on' : 'membership');
            const amount = Number(row.amount || 0);
            const chargedAmount = Number(row.chargedAmount || 0); // bolt-on manual only
            const dueDate = safeToDate(row.dueDate);
            const breakdown = row.breakdown || null; // { baseAmount, platformFee, stripeFee, finalAmount }

            const isBoltOn = paymentType === 'bolt_on';
            const isMembership = !isBoltOn;

            // Row-level fee policy
            const boltOnId = row.boltOnId;
            const boltOnPolicy = isBoltOn ? {
              platformFeeHandling: teamPolicy.platformFeeHandling,
              stripeFeeHandling: boltOnById[boltOnId]?.stripeFeeHandling || teamPolicy.stripeFeeHandling
            } : {
              platformFeeHandling: teamPolicy.platformFeeHandling,
              stripeFeeHandling: teamPolicy.stripeFeeHandling
            };
            let clubExpected = 0;
            let clubPaid = 0;
            if (paid) {
              // PAID: prefer exact stored breakdown
              if (breakdown) {
                clubPaid = payoutFromBreakdown({
                  breakdown,
                  platformFeeHandling: boltOnPolicy.platformFeeHandling,
                  stripeFeeHandling: boltOnPolicy.stripeFeeHandling,
                  currency: row.currency || setupCurrency
                });
              } else {
                // Legacy fallback
                const trustPaidAmount = isBoltOn && method === 'subscription'; // bolt-on subs store "amount = paid to club"
                clubPaid = computePaidClubPayout({
                  amountPence: amount,
                  chargedAmountPence: chargedAmount,
                  isMembership,
                  isBoltOn,
                  platformFeeHandling: boltOnPolicy.platformFeeHandling,
                  stripeFeeHandling: boltOnPolicy.stripeFeeHandling,
                  perCyclePlatformFeePence: feePerCycleFallback,
                  trustPaidAmount,
                  currency: row.currency || setupCurrency
                });
              }
              bumpCycle(cycleId, 'Paid', clubPaid);
              // Add to Expected as well so charts compare like-for-like (target payout)
              bumpCycle(cycleId, 'Expected', clubPaid);
            } else {
              // UNPAID / PROJECTED: prefer breakdown (new projections), else legacy estimate
              if (breakdown) {
                clubExpected = payoutFromBreakdown({
                  breakdown,
                  platformFeeHandling: boltOnPolicy.platformFeeHandling,
                  stripeFeeHandling: boltOnPolicy.stripeFeeHandling,
                  currency: row.currency || setupCurrency
                });
              } else {
                // Legacy: amount is base/catalog
                clubExpected = computeExpectedClubPayout({
                  baseAmountPence: amount,
                  platformFeeHandling: boltOnPolicy.platformFeeHandling,
                  stripeFeeHandling: boltOnPolicy.stripeFeeHandling,
                  perCyclePlatformFeePence: feePerCycleFallback,
                  currency: row.currency || setupCurrency
                });
              }
              bumpCycle(cycleId, 'Expected', clubExpected);

              // Save for "Top Outstanding"
              const label = isBoltOn ? row.label || boltOnById[boltOnId]?.name || 'Bolt-On' : 'Membership';
              outstandingRows.push({
                id: uid,
                amount,
                displayAmount: clubExpected,
                dueDate,
                cycleId,
                kind: isBoltOn ? 'Bolt-On' : 'Membership',
                label
              });
              memberIdsNeeded.add(uid);
            }
          });
        }

        /* ── Build chart data ── */
        const cyclesArr = Object.entries(totalsByCycle).map(([name, {
          Expected,
          Paid
        }]) => ({
          name,
          label: formatCycleLabel(name),
          Expected,
          Paid
        })).sort((a, b) => a.name.localeCompare(b.name));
        setAllCycles(cyclesArr);

        /* ── Fetch user names (chunked) ── */
        const chunk = (arr, n = 10) => {
          const out = [];
          for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
          return out;
        };
        const nameMap = {};
        const ids = Array.from(memberIdsNeeded);
        if (ids.length) {
          const usersCol = collection(db, 'users');
          for (const group of chunk(ids, 10)) {
            const qUsers = query(usersCol, where(documentId(), 'in', group));
            const snap = await getDocs(qUsers);
            snap.forEach(docSnap => {
              const u = docSnap.data() || {};
              const display = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.displayName || u.username || docSnap.id;
              nameMap[docSnap.id] = display;
            });
          }
        }

        // Enrich outstanding + sort by club payout desc
        outstandingRows.sort((a, b) => b.displayAmount - a.displayAmount);
        const enriched = outstandingRows.map(row => ({
          ...row,
          memberName: nameMap[row.id] || row.id
        }));
        setAllOutstanding(enriched);
      } catch (e) {
        setError('Failed to load income data.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, teamId]);
  const yearOptions = useMemo(() => {
    const yrs = new Set(allCycles.map(d => d.name.split('-')[0]));
    return ['All', ...Array.from(yrs)];
  }, [allCycles]);
  const filteredCycles = useMemo(() => {
    let data = allCycles;
    if (selectedYear !== 'All') data = data.filter(d => d.name.startsWith(selectedYear));
    if (selectedMonth !== 'All') data = data.filter(d => d.name.endsWith(`-${selectedMonth}`));
    return data;
  }, [allCycles, selectedYear, selectedMonth]);
  const filteredCyclesForChart = useMemo(() => filteredCycles.slice(-12), [filteredCycles]);
  const filteredTotals = useMemo(() => {
    const expected = filteredCycles.reduce((sum, d) => sum + (d.Expected || 0), 0);
    const paid = filteredCycles.reduce((sum, d) => sum + (d.Paid || 0), 0);
    return {
      expected,
      paid
    };
  }, [filteredCycles]);
  const outstandingFiltered = useMemo(() => {
    if (!allOutstanding.length) return [];
    const allowed = new Set(filteredCycles.map(d => d.name));
    const rows = allOutstanding.filter(r => allowed.has(r.cycleId));
    return rows.slice(0, 5);
  }, [allOutstanding, filteredCycles]);
  const downloadCSV = () => {
    const header = ['Cycle', 'Expected Club Payout (pence)', 'Paid to Club (pence)'];
    const rows = filteredCycles.map(d => [d.name, d.Expected, d.Paid]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    saveAs(blob, `${teamId}-income.csv`);
  };
  if (!isOpen) return null;
  const unpaid = Math.max(0, filteredTotals.expected - filteredTotals.paid);
  const pieData = [{
    name: 'Paid',
    value: filteredTotals.paid
  }, {
    name: 'Unpaid',
    value: unpaid
  }];
  return <div className="modal-backdrop income-modal" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Income Dashboard</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {loading && <p className="muted">Loading financial data…</p>}
        {error && <div className="modal-error">
            {error}{' '}
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => window.location.reload()}>Retry</button>
          </div>}

        {!loading && !error && <>
            <section className="controls-row">
              <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                {yearOptions.map(y => <option key={y} value={y}>
                    {y === 'All' ? 'All Years' : y}
                  </option>)}
              </select>
              <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
                {MONTH_OPTIONS.map(m => <option key={m.value} value={m.value}>
                    {m.label}
                  </option>)}
              </select>
              <button type="button" className="btn btn--secondary btn--sm" onClick={downloadCSV}>Download CSV</button>
            </section>

            <section>
              <h3 className="section-title">Payment Completion (Club Payout)</h3>
              <div className="pie">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                      {pieData.map((entry, idx) => <Cell key={idx} fill={COLORS[idx]} />)}
                    </Pie>
                    <Tooltip formatter={val => formatMinor(val)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section>
              <h3 className="section-title">Monthly Income (Club Payout)</h3>
              <div className="chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={filteredCyclesForChart}>
                    <XAxis dataKey="label" />
                    <YAxis tickFormatter={val => formatMinor(val)} />
                    <Tooltip formatter={val => formatMinor(val)} />
                    <Legend />
                    <Bar dataKey="Expected" name="Expected Club Payout" fill="#8884d8" onClick={({
                  payload
                }) => alert(`Expected in ${payload.label}: ${formatMinor(payload.Expected || 0)}`)} />
                    <Bar dataKey="Paid" name="Paid to Club" fill="#4CAF50" onClick={({
                  payload
                }) => alert(`Paid in ${payload.label}: ${formatMinor(payload.Paid || 0)}`)} />
                    <Brush dataKey="label" height={30} stroke="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section>
              <h3 className="section-title">Top Outstanding Members (Club Payout)</h3>
              {outstandingFiltered.length === 0 ? <p className="muted">No outstanding payments in the selected period.</p> : <table className="table">
                  <thead>
                    <tr>
                      <th>Name & Type</th>
                      <th>Club Payout</th>
                      <th>Due Date</th>
                      <th>Cycle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstandingFiltered.map(row => <tr key={`${row.cycleId}:${row.id}:${row.label}`}>
                        <td>
                          {row.memberName} — {row.kind}
                          <div className="muted">
                            {row.label && row.label !== row.kind ? row.label : null}
                          </div>
                        </td>
                        <td>{formatMinor(row.displayAmount)}</td>
                        <td>
                          {row.dueDate ? row.dueDate.toLocaleDateString('en-GB') : '—'}
                        </td>
                        <td>{row.cycleId || '—'}</td>
                      </tr>)}
                  </tbody>
                </table>}
            </section>
          </>}
      </div>
    </div>;
}
